import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/boards/notifications");
      setItems(r.data || []);
    } catch {
      // silently ignore (e.g. clients/participants have no access)
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const unread = items.filter((n) => !n.read);

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/boards/notifications/${n.notification_id}/read`); } catch { /* noop */ }
    }
    navigate(n.link || `/team-pm?board=${n.board_id}&card=${n.card_id}`);
    load();
  };

  const markAllRead = async () => {
    try { await api.post("/boards/notifications/read-all"); load(); } catch { /* noop */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell-button" className="relative p-2 text-slate-500 hover:text-slate-900 transition-colors">
          <Bell size={18} strokeWidth={1.5} />
          {unread.length > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-white w-80 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
          <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Benachrichtigungen</span>
          {unread.length > 0 && (
            <button onClick={markAllRead} data-testid="notification-mark-all-read" className="text-[10px] text-[#00807E] hover:underline">
              Alle gelesen
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-6">Keine Benachrichtigungen</div>
          )}
          {items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => openNotification(n)}
              data-testid={`notification-item-${n.notification_id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? "bg-[#00CCC9]/5" : ""}`}
            >
              <div className="text-xs text-slate-800">
                {n.title ? <b>{n.title}</b> : <><b>{n.from_user_name}</b> hat dich in <b>„{n.card_title}“</b> erwähnt</>}
              </div>
              <div className="text-[10px] text-slate-400 mt-1 truncate">{n.text}</div>
              <div className="text-[9px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
